import { useState } from "react"
import { IconButton, Stack, Tooltip } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faCopy, faEllipsisV, faInfoCircle, faTrash } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import type { TranslationKey } from "../../../../../../localization/translations"
import { HoverActionMenu } from "../../../../../../../shared/components/table/HoverActionMenu"

const useStyles = makeStyles()((theme) => ({
    handle: {
        width: "1.5rem",
        height: "1.5rem",
        borderRadius: ".25rem",
        color: theme.palette.tailwind.gray[400],
        "& svg": {
            fontSize: ".875rem",
        },
    },
    handleOpen: {
        background: theme.palette.tailwind.gray[100],
    },
    actions: {
        padding: ".125rem .25rem",
    },
    actionButton: {
        width: "1.75rem",
        height: "1.75rem",
        borderRadius: ".375rem",
        color: theme.palette.tailwind.gray[500],
        "& svg": {
            fontSize: ".875rem",
        },
        "&:hover": {
            background: theme.palette.tailwind.gray[100],
            color: theme.palette.text.primary,
        },
    },
}))

type ToolEntry = Readonly<{
    tooltipId: TranslationKey
    /** Fallback text of the tooltip until the translation loads. */
    tooltip: string
    icon: IconDefinition
    isDisabled?: boolean
    onClick: () => void
}>

type Props = Readonly<{
    /** Copy is a no-op on an empty cell. */
    hasContent: boolean
    onDetails: () => void
    /** Absent on theme dishes, which have no other days of the same row to copy to. */
    onCopyToDays?: () => void
    onClear: () => void
}>

/** The cell's action dropdown, on HoverActionMenu so it escapes the scroll area. */
export function CellTools({ hasContent, onDetails, onCopyToDays, onClear }: Props) {
    const { classes, cx } = useStyles()
    const [isMenuOpen, setMenuOpen] = useState(false)

    const closeThen = (closeMenu: () => void, action: () => void) => () => {
        closeMenu()
        action()
    }

    const detailsTool: ToolEntry = {
        tooltipId: "merchant-week-menu-cell-details-tooltip",
        tooltip: "Dish details",
        icon: faInfoCircle,
        onClick: onDetails,
    }
    const copyTool: ToolEntry = {
        tooltipId: "merchant-week-menu-cell-copy-tooltip",
        tooltip: "Copy to other days…",
        icon: faCopy,
        isDisabled: !hasContent,
        onClick: () => onCopyToDays?.(),
    }
    const clearTool: ToolEntry = {
        tooltipId: "merchant-week-menu-cell-clear-tooltip",
        tooltip: "Clear dish",
        icon: faTrash,
        onClick: onClear,
    }

    // Three actions: details, copy to another day, clear.
    const tools: ReadonlyArray<ToolEntry> = [detailsTool, ...(onCopyToDays !== undefined ? [copyTool] : []), clearTool]

    return (
        <LocalizedStrict id="merchant-week-menu-cell-menu-button" attrs={{ "aria-label": true }}>
            <HoverActionMenu
                aria-label="Dish actions"
                icon={<FontAwesomeIcon icon={faEllipsisV} />}
                iconButtonClassName={cx(classes.handle, isMenuOpen && classes.handleOpen)}
                onOpenChange={setMenuOpen}
            >
                {(closeMenu) => (
                    <Stack alignItems="center" spacing=".375rem" className={classes.actions}>
                        {tools.map((tool) => (
                            <LocalizedStrict key={tool.tooltipId} id={tool.tooltipId} attrs={{ title: true }}>
                                <Tooltip arrow placement="left" title={tool.tooltip}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={tool.isDisabled}
                                            className={classes.actionButton}
                                            onClick={closeThen(closeMenu, tool.onClick)}
                                        >
                                            <FontAwesomeIcon icon={tool.icon} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </LocalizedStrict>
                        ))}
                    </Stack>
                )}
            </HoverActionMenu>
        </LocalizedStrict>
    )
}
